import {
  Content,
  ContentTable,
  TDocumentDefinitions,
  StyleDictionary,
  PageSize,
  PageOrientation,
  Margins,
  ContentColumns,
  TableLayout,
  TableCell
} from 'pdfmake/interfaces'
import { sortBy as _sortBy } from 'lodash'

import merinioSvgImage from 'assets/images/merinioLogo'
import { getPunchCards } from './../utilities'
import { parsePunchCards } from './parsePunchCards'

import { t } from 'translations'

interface Options {
  styles: StyleDictionary
  pageSize: PageSize
  pageOrientation: PageOrientation
  pageMargins: Margins
}
const options: Options = {
  styles: {
    defaultStyle: { color: 'grey' },
    title: { fontSize: 14, margin: [0, 0, 0, 10], bold: true },
    subtitle: { fontSize: 10, margin: [0, 0, 0, 10], bold: true },
    generatedBy: { fontSize: 12, margin: [0, 1, 2, 10], color: 'grey', bold: true },
    subHeader: { fontSize: 10, margin: [0, 0, 0, 5] },
    smaller: { fontSize: 7 },
    smallerBold: { bold: true, fontSize: 7 },
    pageNumber: { fontSize: 7, margin: [0, 3, 15, 0] },
    tableStyle: { margin: [0, 0, 0, 12] }
  },
  pageSize: 'A5',
  pageOrientation: 'landscape',
  pageMargins: [15, 15, 30, 30]
}
const layout: TableLayout = {
  // Will make some horizontal lines and vertical lines bold and black or gray and light
  hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length - 1 || i === node.table.body.length ? 1 : 0.25),
  vLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.widths?.length ? 1 : 0.25),
  hLineColor: (i, node) => (i === 0 || i === 1 || i === node.table.body.length - 1 || i === node.table.body.length ? 'black' : 'gray'),
  vLineColor: (i, node) => (i === 0 || i === 1 || i === node.table.widths?.length ? 'black' : 'gray')
}
const subTitle = (range: string): Content => ({ text: [range], style: 'subtitle', alignment: 'left' })

const headerRow = (fields): TableCell[] => fields.map(field => ({ text: t(`manager.punch.pdf.${field.toUpperCase()}`), style: 'smaller', alignment: 'center' }))

const tableRow = (fields, index, line, parsedResult, userId): TableCell[] =>
  fields.map(field => {
    if (field === 'name' && index !== 0) return ''
    // Cell content
    const cell: Content & { rowSpan?: number | undefined } = {
      stack: [{ text: line[field], style: 'smaller', alignment: 'center' }],
      margin: [1, 1, 1, 1]
    }
    // If cell is the name, make on every lines
    if (field === 'name') cell.rowSpan = parsedResult[userId].length
    // Cet the cell color for the difference
    if (field === 'difference') cell.fillColor = line[field] >= 0 ? '#a9dfbf' : '#feaf9a'
    return cell
  })

const lastRow = (parsedResult, userId): TableCell[] => [
  '',
  '',
  '',
  '',
  { text: `${t('manager.punch.pdf.SHIFT_TOTAL')} ${parsedResult[userId][0].totalPlanned}`, style: 'smallerBold', colSpan: 2 },
  '',
  '',
  { text: `${t('manager.punch.pdf.WORKED_TOTAL')} ${parsedResult[userId][0].totalRegular}`, style: 'smallerBold', colSpan: 2 },
  '',
  { text: '' }
]
const emptyLine: Content = { text: [' '], style: 'smaller', margin: [0, 0, 0, 10] }

const pageHeader = (): ContentColumns => ({
  columns: [
    { text: t('manager.punch.pdf.TITLE'), style: 'title', alignment: 'left' },
    { text: [t('manager.punch.pdf.GENERATED_BY')], style: 'generatedBy', alignment: 'right' },
    { image: merinioSvgImage, width: 60 }
  ]
})

export const exportPunchCardsPDF = async ({ filter = {}, sortBy }: { filter: Record<string, unknown>; sortBy: string }): Promise<void> => {
  const [punchCards, range] = await getPunchCards({ filter, sortBy })

  const fields = ['name', 'date', 'position', 'day', 'shift', 'shiftTotal', 'worked', 'workedRounded', 'workedTotal', 'difference']

  const parsedResult = parsePunchCards(punchCards)

  const documentDefinition: TDocumentDefinitions = {
    ...options,
    content: [],
    footer: (currentPage, pageCount) => ({ text: currentPage.toString() + ' / ' + pageCount, style: 'pageNumber', alignment: 'right' })
  }

  if (Array.isArray(documentDefinition.content)) documentDefinition.content.push(pageHeader())
  if (Array.isArray(documentDefinition.content)) documentDefinition.content.push(subTitle(range))

  // Data is on the form of (hashMap) userId:<array of user's punch card>
  // For each users, draw a table
  for (const userId in parsedResult) {
    // this needs to implement a stack instead TS does not reconize unbreakable  https://github.com/bpampuch/pdfmake/issues/1228#issuecomment-354411288
    const table: ContentTable & { unbreakable: boolean } = {
      table: {
        headerRows: 1,
        widths: ['*', ...Array(fields.length - 1).fill('auto')],
        body: []
      },
      layout,
      unbreakable: parsedResult[userId].length < 20 ? true : false
    }

    table.table.body.push(headerRow(fields))

    parsedResult[userId].forEach((line, index) => {
      const row = tableRow(fields, index, line, parsedResult, userId)
      table.table.body.push(row)
    })
    table.table.body.push(lastRow(parsedResult, userId))

    if (Array.isArray(documentDefinition.content)) documentDefinition.content.push(table)
    if (Array.isArray(documentDefinition.content)) documentDefinition.content.push(emptyLine)
  }

  const pdfMake = (await import('pdfmake/build/pdfmake.min')).default
  const pdfFonts = (await import('pdfmake/build/vfs_fonts')).default
  pdfMake.vfs = pdfFonts.pdfMake.vfs

  // Print the PDF
  pdfMake.createPdf(documentDefinition).download(`${t('manager.punch.pdf.PDF_NAME', { range })}.pdf`)
}
